import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Col,
  CustomInput,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  Row,
} from 'reactstrap';

const OPERATOR_BRAND = {
  UBER: { bg: '#111827', color: '#FFFFFF', initials: 'Ub' },
  LALAMOVE: { bg: '#FDE2CE', color: '#C2410C', initials: 'LL' },
  ENTREGA99: { bg: '#FEF3C7', color: '#A16207', initials: '99' },
  IFOOD_ENVIOS: { bg: '#c70202', color: '#FFFFFF', initials: 'IF' },
  BEE: { bg: '#FDE68A', color: '#92400E', initials: 'Bee' },
  VIDDY_DELIVERIES: { bg: '#2544b1', color: '#FFFFFF', initials: 'VE' },
};

const VEHICLE_ICON = {
  M: 'las la-motorcycle',
  M_PRO: 'las la-motorcycle',
  C: 'las la-car-side',
  UV_FIORINO: 'las la-truck-pickup',
};

const DEFAULT_BRAND = { bg: '#E5E7EB', color: '#4B5563', initials: '?' };

/** Impede que o clique em controles inicie o arraste da linha. */
const stopDrag = event => event.stopPropagation();

function VehicleSelect({ item, vehicleLabels, disabled, onToggleVehicle }) {
  const [open, setOpen] = useState(false);

  const supported = item.supported_vehicles || Object.keys(vehicleLabels);
  const selected = item.vehicles || [];
  const isEmpty = item.enabled && !selected.length;

  return (
    <div onPointerDown={stopDrag}>
      <Dropdown isOpen={open} toggle={() => setOpen(v => !v)}>
        <DropdownToggle
          caret
          color="link"
          disabled={disabled || !item.enabled}
          className={`w-100 d-flex align-items-center justify-content-between border rounded px-2 py-1 text-decoration-none ${
            isEmpty ? 'border-danger' : ''
          }`}
          style={{
            background: '#FFFFFF',
            color: '#374151',
            boxShadow: 'none',
          }}
        >
          <span className="d-flex align-items-center">
            {selected.length ? (
              selected.map(code => (
                <i
                  key={code}
                  className={`${
                    VEHICLE_ICON[code] || 'las la-truck'
                  } font-size-18 mr-1`}
                  style={{ color: '#6B7280' }}
                />
              ))
            ) : (
              <span className="font-size-12 text-danger">Nenhum</span>
            )}
            {selected.length > 0 && (
              <span
                className="badge badge-pill ml-1"
                style={{ background: '#EEF2FF', color: '#4338CA' }}
              >
                {selected.length}
              </span>
            )}
          </span>
        </DropdownToggle>

        <DropdownMenu right className="py-2" style={{ minWidth: 200 }}>
          {supported.map(code => (
            <div key={code} className="px-3 py-1">
              <CustomInput
                type="checkbox"
                id={`${item.operator}-vehicle-${code}`}
                checked={selected.includes(code)}
                onChange={() => onToggleVehicle(code)}
                label={
                  <span className="d-flex align-items-center">
                    <i
                      className={`${
                        VEHICLE_ICON[code] || 'las la-truck'
                      } font-size-18 mr-2`}
                      style={{ color: '#6B7280' }}
                    />
                    {vehicleLabels[code] || code}
                  </span>
                }
              />
            </div>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}

function SortableOperator({
  item,
  displayPriority,
  vehicleLabels,
  disabled,
  incentiveStep,
  onToggleEnabled,
  onToggleVehicle,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.operator, disabled });

  const brand = OPERATOR_BRAND[item.operator] || DEFAULT_BRAND;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.9 : 1,
    boxShadow: isDragging ? '0 8px 24px rgba(17,24,39,.12)' : 'none',
    background: item.enabled ? '#FFFFFF' : '#F9FAFB',
    cursor: disabled ? 'default' : 'grab',
    touchAction: 'none',
    zIndex: isDragging ? 10 : 'auto',
    position: 'relative',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border rounded px-2 py-3 mb-2"
      {...attributes}
      {...listeners}
    >
      <Row className="w-100 mx-0 align-items-center">
        <Col xs={3} lg={1} className="px-0">
          <div className="d-flex align-items-center">
            <i
              className="las la-grip-lines font-size-18 mr-2"
              style={{ color: '#D1D5DB' }}
              title="Arraste para reordenar"
            />
            <span
              className="d-inline-flex align-items-center justify-content-center rounded font-size-12 font-weight-600"
              style={{
                width: 24,
                height: 24,
                background: item.enabled ? '#EEF2FF' : '#F3F4F6',
                color: item.enabled ? '#4338CA' : '#9CA3AF',
              }}
            >
              {displayPriority || '—'}
            </span>
          </div>
        </Col>

        <Col xs={9} lg={4} className="px-0">
          <div className="d-flex align-items-center">
            <span
              className="d-inline-flex align-items-center justify-content-center rounded mr-2 font-size-12 font-weight-700"
              style={{
                width: 34,
                height: 34,
                background: brand.bg,
                color: brand.color,
                opacity: item.enabled ? 1 : 0.5,
                flexShrink: 0,
              }}
            >
              {brand.initials}
            </span>
            <div>
              <div
                className={`font-weight-600 ${
                  item.enabled ? 'text-gray-700' : 'text-muted'
                }`}
              >
                {item.label}
              </div>
              <div className="font-size-12 text-muted">
                {item.supports_incentive
                  ? 'Aceita incentivo na busca'
                  : 'Sem incentivo — rebusca cancela e refaz'}
              </div>
            </div>
          </div>
        </Col>

        <Col xs={12} lg={3} className="px-0 mt-2 mt-lg-0 pr-lg-3">
          <VehicleSelect
            item={item}
            vehicleLabels={vehicleLabels}
            disabled={disabled}
            onToggleVehicle={code => onToggleVehicle(item.operator, code)}
          />
        </Col>

        <Col xs={6} lg={2} className="px-0">
          <span
            className="badge badge-pill px-2 py-1 font-weight-500"
            style={
              item.enabled
                ? { background: '#DCFCE7', color: '#15803D' }
                : { background: '#F3F4F6', color: '#9CA3AF' }
            }
          >
            ● {item.enabled ? 'Ativo' : 'Inativo'}
          </span>
        </Col>

        <Col xs={6} lg={2} className="px-0 text-right">
          <div onPointerDown={stopDrag} className="d-inline-block">
            <CustomInput
              type="switch"
              id={`operator-enabled-${item.operator}`}
              label=""
              className="text-primary"
              checked={item.enabled}
              disabled={disabled}
              onChange={() => onToggleEnabled(item.operator)}
            />
          </div>
        </Col>
      </Row>

      {item.enabled && !(item.vehicles || []).length && (
        <div className="text-danger font-size-12 mt-2 pl-lg-3">
          Selecione ao menos um veículo — sem isso o parceiro nunca será
          acionado.
        </div>
      )}

      {item.enabled && item.available_for_store === false && (
        <div className="text-danger font-size-12 mt-2 pl-lg-3">
          <i className="las la-exclamation-circle mr-1" />
          {item.unavailable_reason}
        </div>
      )}

      {item.enabled &&
        item.minimum_incentive > 0 &&
        incentiveStep > 0 &&
        incentiveStep < item.minimum_incentive && (
          <div
            className="font-size-12 mt-2 pl-lg-3"
            style={{ color: '#B45309' }}
          >
            <i className="las la-exclamation-triangle mr-1" />
            {item.label} exige incentivo mínimo de R${' '}
            {Number(item.minimum_incentive).toFixed(2).replace('.', ',')}. Com o
            valor atual, a primeira rebusca será recusada.
          </div>
        )}
    </div>
  );
}

export default function DeliveryOperatorsList({
  operators,
  onChange,
  vehicleLabels,
  incentiveStep = 0,
  disabled = false,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = operators.findIndex(o => o.operator === active.id);
    const newIndex = operators.findIndex(o => o.operator === over.id);

    if (oldIndex < 0 || newIndex < 0) return;

    onChange(arrayMove(operators, oldIndex, newIndex));
  };

  const handleToggleEnabled = operator => {
    onChange(
      operators.map(item => {
        if (item.operator !== operator) return item;

        const enabled = !item.enabled;
        const supported = item.supported_vehicles || Object.keys(vehicleLabels);

        // Ao ativar sem veículos definidos, marca todos os suportados.
        const vehicles =
          enabled && !(item.vehicles || []).length ? supported : item.vehicles;

        return { ...item, enabled, vehicles };
      })
    );
  };

  const handleToggleVehicle = (operator, vehicle) => {
    onChange(
      operators.map(item => {
        if (item.operator !== operator) return item;

        const current = item.vehicles || [];
        const vehicles = current.includes(vehicle)
          ? current.filter(v => v !== vehicle)
          : [...current, vehicle];

        return { ...item, vehicles };
      })
    );
  };

  if (!operators.length) {
    return (
      <div className="text-muted font-size-14 py-3">
        Nenhum parceiro de entrega com chave cadastrada neste ambiente. Cadastre
        as chaves em Configurações para habilitar o roteador.
      </div>
    );
  }

  // A numeração exibida conta apenas os parceiros ativos.
  let activeCount = 0;
  const withPriority = operators.map(item => {
    if (item.enabled) {
      activeCount += 1;
      return { item, displayPriority: activeCount };
    }
    return { item, displayPriority: null };
  });

  return (
    <>
      <Row className="w-100 mx-0 px-2 mb-2 d-none d-lg-flex">
        <Col lg={1} className="px-0 font-size-12 text-muted text-uppercase">
          Prior.
        </Col>
        <Col lg={4} className="px-0 font-size-12 text-muted text-uppercase">
          Transportadora
        </Col>
        <Col lg={3} className="px-0 font-size-12 text-muted text-uppercase">
          Veículos
        </Col>
        <Col lg={2} className="px-0 font-size-12 text-muted text-uppercase">
          Status
        </Col>
        <Col
          lg={2}
          className="px-0 font-size-12 text-muted text-uppercase text-right"
        >
          Ativar
        </Col>
      </Row>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={operators.map(o => o.operator)}
          strategy={verticalListSortingStrategy}
        >
          {withPriority.map(({ item, displayPriority }) => (
            <SortableOperator
              key={item.operator}
              item={item}
              displayPriority={displayPriority}
              vehicleLabels={vehicleLabels}
              incentiveStep={incentiveStep}
              disabled={disabled}
              onToggleEnabled={handleToggleEnabled}
              onToggleVehicle={handleToggleVehicle}
            />
          ))}
        </SortableContext>
      </DndContext>
    </>
  );
}
